class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if ((window.location.pathname.includes('/search') || (searchParams && searchParams.includes('q='))) && searchParams && !searchParams.includes('type=')) {
      searchParams += '&type=product';
    }
    // Detect fallback mode (showing /collections/all on the no-results search page)
    // BEFORE appending type=product — collection URLs must not receive type=product.
    const productGrid = document.getElementById('product-grid');
    const fallbackBase = productGrid ? productGrid.dataset.fallbackBaseUrl : null;

    if (!fallbackBase && (window.location.pathname.includes('/search') || (searchParams && searchParams.includes('q='))) && searchParams && !searchParams.includes('type=')) {
      searchParams += '&type=product';
    }
    // Keep the quick-delivery view (?view=60-mins / blr-now) on every filter fetch and URL update,
    // otherwise the re-rendered grid is built without it and product links lose the view.
    const currentUrlParams = new URLSearchParams(window.location.search);
    const currentView = currentUrlParams.get('view');
    if (currentView && !/(^|&)view=/.test(searchParams || '')) {
      searchParams = (searchParams ? searchParams + '&' : '') + 'view=' + encodeURIComponent(currentView);
    }
    ['zone1', 'zone2', 'zone3'].forEach((z) => {
      if (currentUrlParams.has(z) && !new RegExp('(^|&)' + z + '(=|&|$)').test(searchParams || '')) {
        searchParams = (searchParams ? searchParams + '&' : '') + z;
      }
    });
    if (currentUrlParams.has('zone') && !/(^|&)zone=/.test(searchParams || '')) {
      searchParams = (searchParams ? searchParams + '&' : '') + 'zone=' + encodeURIComponent(currentUrlParams.get('zone'));
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.classList.add('is-loading');
      const coll = gridContainer.querySelector('.collection');
      if (coll) coll.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const basePath = fallbackBase || window.location.pathname;
      const url = `${basePath}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        try {
          FacetFiltersForm.renderFilters(html, event);
        } catch (err) {
          console.warn('FacetFiltersForm.renderFilters error:', err);
        }
        try {
          FacetFiltersForm.renderProductGridContainer(html);
        } catch (err) {
          console.warn('FacetFiltersForm.renderProductGridContainer error:', err);
        }
        try {
          FacetFiltersForm.renderProductCount(html);
        } catch (err) {
          console.warn('FacetFiltersForm.renderProductCount error:', err);
        }
        if (typeof initializeScrollAnimationTrigger === 'function') {
          try {
            initializeScrollAnimationTrigger(html.innerHTML);
          } catch (e) {}
        }
      })
      .catch((error) => {
        console.error('FacetFiltersForm fetch error:', error);
      })
      .finally(() => {
        const gridContainer = document.getElementById('ProductGridContainer');
        if (gridContainer) gridContainer.classList.remove('is-loading');
        const coll = document.querySelector('.collection');
        if (coll) coll.classList.remove('loading');
        const countContainer = document.getElementById('ProductCount');
        if (countContainer) countContainer.classList.remove('loading');
        const countContainerDesktop = document.getElementById('ProductCountDesktop');
        if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      try {
        FacetFiltersForm.renderFilters(html, event);
      } catch (err) {
        console.warn('FacetFiltersForm.renderFilters error:', err);
      }
      try {
        FacetFiltersForm.renderProductGridContainer(html);
      } catch (err) {
        console.warn('FacetFiltersForm.renderProductGridContainer error:', err);
      }
      try {
        FacetFiltersForm.renderProductCount(html);
      } catch (err) {
        console.warn('FacetFiltersForm.renderProductCount error:', err);
      }
      if (typeof initializeScrollAnimationTrigger === 'function') {
        try {
          initializeScrollAnimationTrigger(html.innerHTML);
        } catch (e) {}
      }
    } finally {
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) gridContainer.classList.remove('is-loading');
      const coll = document.querySelector('.collection');
      if (coll) coll.classList.remove('loading');
      const countContainer = document.getElementById('ProductCount');
      if (countContainer) countContainer.classList.remove('loading');
      const countContainerDesktop = document.getElementById('ProductCountDesktop');
      if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }
  }

  static renderProductGridContainer(html) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsedHtml.getElementById('ProductGridContainer');
    if (newGrid) {
      gridContainer.innerHTML = newGrid.innerHTML;
    }
    gridContainer.classList.remove('is-loading');

    // Safety net: make sure quick-view product links keep the current ?view=
    const pageView = new URLSearchParams(window.location.search).get('view');
    if (pageView) {
      gridContainer.querySelectorAll('a.blr-card__media-link, a.blr-card__title-link').forEach((link) => {
        const href = link.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;
        const u = new URL(href, window.location.origin);
        u.searchParams.set('view', pageView);
        link.setAttribute('href', u.pathname + u.search + u.hash);
      });
    }

    gridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    if (window.ZoneDelivery && typeof window.ZoneDelivery.applyState === 'function') {
      window.ZoneDelivery.applyState();
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : null;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container && count !== null) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop && count !== null) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement && elementToRender.parentElement.id) {
          const siblingFilter = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (siblingFilter) {
            siblingFilter.before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const el = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (el && target) {
        target.innerHTML = el.innerHTML;
      }
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const drawer = mobileForm.closest('menu-drawer');
      if (drawer && typeof drawer.bindEvents === 'function') {
        drawer.bindEvents();
      }
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    if ((window.location.pathname.includes('/search') || (searchParams && searchParams.includes('q='))) && searchParams && !searchParams.includes('type=')) {
      searchParams += '&type=product';
    }
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  // onSubmitHandler(event) {
  //   event.preventDefault();
  //   const sortFilterForms = document.querySelectorAll('facet-filters-form form');
  //   if (event.srcElement.className == 'mobile-facets__checkbox') {
  //     const searchParams = this.createSearchParams(event.target.closest('form'));
  //     this.onSubmitForm(searchParams, event);
  //   } else {
  //     const forms = [];
  //     const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

  //     sortFilterForms.forEach((form) => {
  //       if (!isMobile) {
  //         if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
  //           forms.push(this.createSearchParams(form));
  //         }
  //       } else if (form.id === 'FacetFiltersFormMobile') {
  //         forms.push(this.createSearchParams(form));
  //       }
  //     });
  //     this.onSubmitForm(forms.join('&'), event);
  //   }
  // }

  onSubmitHandler(event) {
    event.preventDefault();

    if (event.srcElement && event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });

    const newParams = new URLSearchParams(forms.join('&'));
    const currentUrlParams = new URLSearchParams(window.location.search);
    ['display_q'].forEach((key) => {
      if (currentUrlParams.has(key) && !newParams.has(key)) {
        newParams.set(key, currentUrlParams.get(key));
      }
    });

    this.onSubmitForm(newParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
